import { AfterViewInit, Component, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import * as fileSaver from 'file-saver';
import { SnackbarService } from 'src/app/shared/services/snackbar.service';
import { IEvaluationResponse } from 'src/app/shared/models/evaluation';
import { EvaluationFormComponent } from './evaluation-form/evaluation-form.component';
import { EvaluationService } from './evaluation-service';
import { IApplication } from 'src/app/shared/models/fiche-expression-besoin';
import { AuthenticationService } from 'src/app/shared/services/authentication.service';
import { CurrentUserService } from 'src/app/shared/services/current-user.service';
import { DialogService } from 'src/app/shared/services/dialog.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-evaluation-list',
  templateUrl: './evaluation-list.component.html',
  styleUrls: ['./evaluation-list.component.less']
})
export class EvaluationListComponent implements OnInit, AfterViewInit, OnDestroy {

  dataSource = new MatTableDataSource<IEvaluationResponse>();
  displayedColumns: string[] = ['nom', 'prenom', 'application', 'uo', 'dateDemarrage', 'status', 'actions'];
  loading = false;
  private destroy$ = new Subject<void>();

  @ViewChild(MatSort) sort!: MatSort;

  constructor(
    private evaluationService: EvaluationService,
    private dialog: MatDialog,
    private router: Router,
    private snackbarService: SnackbarService,
    private _liveAnnouncer: LiveAnnouncer,
    private authenticationService: AuthenticationService,
    private currentUserService: CurrentUserService,
    private dialogService: DialogService
  ) {}

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.loadEvaluations();
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
    this.dataSource.sortingDataAccessor = (item, property) => {
      switch (property) {
        case 'dateDemarrage': return new Date(item.dateDemarrage).getTime();
        default:              return (item as any)[property];
      }
    };
    this.dataSource.filterPredicate = (item, filter) => {
      const searchStr = [
        item.nom, item.prenom, item.application,
        item.uniteOrganisationnelle?.code ?? ''
      ].join(' ').toLowerCase();
      return searchStr.includes(filter);
    };
  }

  announceSortChange(sortState: Sort): void {
    if (sortState.direction) {
      this._liveAnnouncer.announce(
        `Tri ${sortState.direction === 'asc' ? 'ascendant' : 'descendant'}`
      );
    } else {
      this._liveAnnouncer.announce('Tri réinitialisé');
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Chargement ────────────────────────────────────────────────────────────

  loadEvaluations(): void {
    const user = this.currentUserService.getCurrentUser();
    const apps: IApplication[] = this.currentUserService.getUserApplications();
    let evals$;

    if (this.authenticationService.isUserAdminOrServiceManagerOrManagerCDS()) {
      evals$ = this.evaluationService.getAll();
    } else {
      evals$ = this.evaluationService.getByApplications(apps);
    }

    evals$.subscribe({
      next: (evaluations) => {
        this.dataSource.data = evaluations;
        this.loading = false;
      },
      error: (err) => {
        this.snackbarService.openSnackBar(
          this.extractErrorMessage(err, 'Erreur lors du chargement des évaluations'),
          'error-snack-bar'
        );
        this.loading = false;
      }
    });
  }

  // ── Filtre ────────────────────────────────────────────────────────────────

  applyFilter(event: Event): void {
    this.dataSource.filter = (event.target as HTMLInputElement).value.trim().toLowerCase();
  }

  // ── Navigation ────────────────────────────────────────────────────────────

  openEvaluation(evaluation: IEvaluationResponse): void {
    this.router.navigate(['/core/evaluations', evaluation.id]);
  }

  // ── Création ─────────────────────────────────────────────────────────────

  openCreateDialog(): void {
    const dialogRef = this.dialog.open(EvaluationFormComponent, {
      width: '670px',
      maxWidth: '95vw',
      data: null
    });

    dialogRef.afterClosed().subscribe((result: IEvaluationResponse | undefined) => {
      if (result) {
        // Navigation directe vers le tableau de l'évaluation créée
        this.router.navigate(['/core/evaluations', result.id]);
      }
    });
  }

  // ── Suppression totale ────────────────────────────────────────────────────

  deleteEvaluation(evaluation: IEvaluationResponse): void {
    const fullName = `${evaluation.prenom ?? ''} ${evaluation.nom ?? ''}`.trim();

    this.dialogService
      .openConfirmDialog(
        fullName ? `Supprimer complètement l'évaluation de ${fullName} ?` : 'Supprimer complètement cette évaluation ?',
        'Suppression totale'
      )
      .afterClosed()
      .subscribe((confirmed: boolean) => {
        if (!confirmed) {
          return;
        }

        this.evaluationService.delete(evaluation.id).subscribe({
          next: () => {
            this.snackbarService.openSnackBar('L’évaluation a été supprimée avec succès');
            this.loadEvaluations();
          },
          error: (err) => {
            this.snackbarService.openSnackBar(
              this.extractErrorMessage(err, 'Erreur lors de la suppression de l\'évaluation'),
              'error-snack-bar'
            );
          }
        });
      });
  }

   // ── Export ────────────────────────────────────────────────────────────────
 
  exportData(): void {
    this.evaluationService.exportData().subscribe({
      next: (data) => {
        if (!data) {
          this.snackbarService.openSnackBar('Aucune évaluation à exporter', 'error-snack-bar');
          return;
        }
        const blob = new Blob([data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        fileSaver.saveAs(
          blob,
          'Evaluations_Competences_' +
          new Date().toLocaleDateString().replace(/\//g, '-') +
          '.xlsx'
        );
        this.snackbarService.openSnackBar('Les évaluations ont été exportées avec succès');
      },
      error: (err) => {
        this.snackbarService.openSnackBar(
          this.extractErrorMessage(err, 'Erreur lors de l\'export des évaluations'),
          'error-snack-bar'
        );
      }
    });
  }
 

  // ── Helper erreurs ────────────────────────────────────────────────────────

  private extractErrorMessage(err: any, fallback: string): string {
    const body = err?.error;
    if (body?.message && typeof body.message === 'string') return body.message;
    if (typeof body === 'string') return body;
    return fallback;
  }
}